import { Box, Typography, Container, Card, CardContent, Chip, Grid } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { PublicService } from '../../services/publicApi';

interface LandingPageServicesProps {
  services: PublicService[];
  primaryColor: string;
}

export const LandingPageServices = ({ services, primaryColor }: LandingPageServicesProps) => {
  if (!services || services.length === 0) return null;

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatPrice = (priceInCents: number): string => {
    // Price is stored in cents, convert to dollars
    const priceInDollars = priceInCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: priceInDollars % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(priceInDollars);
  };

  return (
    <Box
      component="section"
      id="services"
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'grey.50',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: 1,
            textAlign: 'center',
            color: 'grey.900',
          }}
        >
          Services
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'grey.600',
            mb: 5,
            textAlign: 'center',
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          Here's how I can help you reclaim your time
        </Typography>

        <Grid container spacing={3}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service._id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: primaryColor,
                    boxShadow: `0 4px 20px ${primaryColor}15`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Category Badge */}
                  {service.category && (
                    <Chip
                      label={service.category}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: `${service.color || primaryColor}15`,
                        color: service.color || primaryColor,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  )}

                  {/* Service Name */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: 'grey.900',
                    }}
                  >
                    {service.name}
                  </Typography>

                  {/* Description */}
                  {service.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'grey.600',
                        mb: 2,
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {service.description}
                    </Typography>
                  )}

                  {/* Duration & Price */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto',
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'grey.100',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                      <Typography variant="body2" color="grey.600">
                        {formatDuration(service.duration)}
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: primaryColor,
                      }}
                    >
                      {formatPrice(service.price)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
